import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './Contact.scss';
import Seo from '../../../components/reusable/Seo/Seo.js';
import PageHero from '../../../components/ui/PageHero.js';
import Reveal from '../../../components/ui/Reveal.js';
import ContactForm from '../../../components/reusable/ContactForm/ContactForm.js';
import { company } from '../../../content/site.js';

/**
 * /contact, short, utility-grade contact page for the two audiences that
 * matter most: physicians exploring a fit and hospital leaders evaluating
 * a partner. `?audience=facility` (and friends) presets the form's
 * "Which describes you best?" select via ContactForm's defaultDescription.
 *
 * Values must match `optionsForWhatsDescribesYou` in Common/Constant.js.
 */
const AUDIENCE_TO_DESCRIPTION = {
  facility: 'Hospital or health-system leader',
  clinician: 'Physician (PM&R)',
  patient: 'Patient or family member',
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const audience = searchParams.get('audience');
  const defaultDescription = AUDIENCE_TO_DESCRIPTION[audience] || '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="contact">
      <Seo
        title="Contact"
        description="Reach Virginia Rehab Physicians. Physicians exploring a fit and hospital leaders evaluating a rehab partner can call, email, or send a message. It goes straight to our team."
        path="/contact"
      />

      <PageHero
        eyebrow="Contact"
        title={
          <>
            Let&rsquo;s <em>talk.</em>
          </>
        }
        lede="Physicians: if you're weighing what practice should feel like, we'd like to compare notes. Hospital leaders: if you're evaluating a rehab partner, start the conversation here."
      />

      <section className="u-section contact__main">
        <div className="u-container">
          <div className="contact__layout">
            {/* ---- Form ---- */}
            <Reveal className="contact__form-col">
              <div className="contact__form-panel">
                <h2 className="contact__form-title">Send a message</h2>
                <p className="contact__form-sub">
                  Tell us who you are and what you need. It lands with a person,
                  not a queue.
                </p>
                <ContactForm
                  className="contact__form"
                  defaultDescription={defaultDescription}
                />
              </div>
            </Reveal>

            {/* ---- Right rail ---- */}
            <div className="contact__rail">
              <Reveal delay={0.08}>
                <aside
                  className="contact__details"
                  aria-label="Direct contact details"
                >
                  <span className="u-eyebrow">Reach us directly</span>
                  <dl className="contact__details-list">
                    <div className="contact__detail">
                      <dt>Phone</dt>
                      <dd>
                        <a href={company.phoneHref}>{company.phone}</a>
                      </dd>
                    </div>
                    <div className="contact__detail">
                      <dt>Email</dt>
                      <dd>
                        <a href={`mailto:${company.email}`}>{company.email}</a>
                      </dd>
                    </div>
                  </dl>
                </aside>
              </Reveal>

              <Reveal delay={0.16}>
                <div className="contact__audience">
                  <h3>Physicians &amp; APPs</h3>
                  <p>
                    Exploring a physician-led PM&amp;R practice? See where
                    we&rsquo;re growing and what the work looks like.
                  </p>
                  <Link to="/careers" className="contact__audience-link">
                    Explore careers <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </Reveal>

              <Reveal delay={0.22}>
                <div className="contact__audience contact__audience--dark">
                  <h3>Hospital &amp; health-system leaders</h3>
                  <p>
                    Evaluating rehab coverage or medical direction? The form on
                    this page reaches our practice leadership directly.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Reveal as="aside" className="contact__footnote" y={12}>
        <div className="u-container">
          <p>
            Across Virginia <span aria-hidden="true">•</span> expanding to
            new markets
          </p>
        </div>
      </Reveal>
    </div>
  );
}
